import { AlertComponent } from 'Components/HelperComponents/AlertComponent'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { ProposalsList } from 'NewVersion/components/Proposal/ProposalsList'
import Button from 'NewVersion/components/UI/Buttons/Button'
import Chips from 'NewVersion/components/UI/Chips/Chips'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import { EditIcon } from 'NewVersion/icons/EditIcon'
import 'NewVersion/styles/Dashboard.scss'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import * as path from 'Routes/Paths'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'

/**
 * ArtistDashboard - Dashboard for artists without representant
 * Shows artist info, representant (if any), and list of proposals
 */
export const ArtistDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { artistId } = useParams()
  const { edition, currentEdition, loading: editionLoading } = useEdition()

  // Fetch artist data
  const {
    data: artist,
    isLoading: artistLoading,
    error: artistError,
  } = useGuillotina({
    path: artistId ? `${edition}/${artistId}` : null,
  })

  // Fetch proposals for this artist
  const { data: proposalsResult, isLoading: proposalsLoading } = useGuillotina({
    path: artistId
      ? `${edition}/${artistId}/@search?type_name=Proposta&_sort_des=creation_date`
      : null,
  })

  const proposals = proposalsResult?.items || []

  if (editionLoading || artistLoading) {
    return <LoadingComponent />
  }

  if (artistError || !artist) {
    return <AlertComponent message={t('error_loading_artist')} type="error" />
  }

  const handleNewProposal = () => {
    navigate(
      `${path.PROPOSALS_FORM_PATH}?type=${ENS_TYPES_OBJ.artista.text}&artistId=${edition}/${artistId}&from=gestio`
    )
  }

  const handleEditArtist = () => {
    navigate(
      `${path.EDIT_ENTITY_FORM_PATH.replace(':year', edition).replace(
        ':entityId',
        artistId
      )}?from=gestio&artistId=${artistId}`
    )
  }

  const handleBack = () => {
    navigate(path.PROPOSALS_GESTIO_PATH)
  }

  return (
    <div className="dashboard">
      <GoBackButton onClick={handleBack} text={t('change_profile')} />

      {/* Artist Header */}
      <div className="dashboard__header">
        <div className="dashboard__avatar">{artist.title?.[0]?.toUpperCase() || '?'}</div>
        <div className="dashboard__info">
          <p className="dashboard__title">{artist.title}</p>
          <p className="dashboard__email">{artist.correu}</p>
          <p className="dashboard__extra-info">
            {artist.representant
              ? `${t('representant')}: ${
                  artist.title_representant || artist.representant.split('/').pop()
                }`
              : t('sense_representant_assignat')}
          </p>
        </div>
        <Chips
          text={t('editar')}
          icon={<EditIcon />}
          onClick={handleEditArtist}
          dataTest="btnEditArtist"
        />
      </div>

      {/* Proposals Section */}
      <div className="dashboard__section">
        <div className="dashboard__section-header">
          <p className="dashboard__section-title">{t('les_teves_propostes')}</p>
          {!currentEdition?.callIsFinished() && (
            <Button
              type="button"
              color="primary"
              onClick={handleNewProposal}
              dataTest="btnNewProposal"
            >
              + {t('nova_proposta')}
            </Button>
          )}
        </div>

        {proposalsLoading ? (
          <LoadingComponent />
        ) : (
          <ProposalsList
            proposals={proposals}
            canSubmitProposal={!currentEdition?.callIsFinished()}
          />
        )}
      </div>
    </div>
  )
}

export default ArtistDashboard
